// Competitive Intelligence API Service
import competitiveResearchWebSocketService from './CompetitiveResearchWebSocketService';

import config from '../utils/config';

const API_BASE_URL = config.competitiveIntelApiUrl() || 'https://content-api-beta.ci-apps.gdsp.aws.dev';

export const competitiveIntelligenceApi = {
  // Health check
  healthCheck: async () => {
    const response = await fetch(`${API_BASE_URL}/health`);
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }
    return response.json();
  },

  // Get available models
  getModels: async () => {
    const response = await fetch(`${API_BASE_URL}/models`);
    if (!response.ok) {
      throw new Error(`Failed to get models: ${response.status}`);
    }
    return response.json();
  },

  // Start research request (updated for new architecture)
  // Returns: { task_id, status, message, events_url }
  startResearch: async (researchData) => {
    const response = await fetch(`${API_BASE_URL}/research`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(researchData)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to start research: ${response.status} - ${errorText}`);
    }
    
    return response.json(); // Contains task_id for tracking
  },

  // Get task status (updated for new architecture)
  getTaskStatus: async (taskId) => {
    const response = await fetch(`${API_BASE_URL}/research/${taskId}/status`);
    if (!response.ok) {
      throw new Error(`Failed to get task status: ${response.status}`);
    }
    return response.json();
  },

  // Send clarification message (updated for new architecture)
  sendClarification: async (taskId, message) => {
    const response = await fetch(`${API_BASE_URL}/research/${taskId}/clarify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ answers: message })  // Server expects 'answers' field
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to send clarification: ${response.status} - ${errorText}`);
    }
    
    return response.json();
  },

  // Approve research brief (updated for new architecture)
  approveResearch: async (taskId, action = 'approved') => {
    const response = await fetch(`${API_BASE_URL}/research/${taskId}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ action })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to approve research: ${response.status} - ${errorText}`);
    }
    
    return response.json();
  },

  // Note: Sessions list endpoint removed in new architecture
  // Each research request is now a self-contained task

  // Create SSE connection for real-time updates (updated for new architecture)
  createSSEConnection: (taskId, onMessage, onError) => {
    const eventSource = new EventSource(`${API_BASE_URL}/research/${taskId}/events`);
    
    eventSource.onopen = (event) => {
      console.log('SSE connection opened:', event);
    };
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('SSE message parsed:', data);
        onMessage(data);
      } catch (err) {
        console.error('Error parsing SSE message:', err, 'Raw data:', event.data);
        onError(err);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error, 'ReadyState:', eventSource.readyState);
      
      // Only call onError if the connection is actually broken
      if (eventSource.readyState === EventSource.CLOSED) {
        onError(error);
      }
    };

    return eventSource;
  },

  // WebSocket-based research methods (direct API Gateway routes)
  startResearchWebSocket: (researchData) => {
    console.log('Starting research via WebSocket:', researchData);
    competitiveResearchWebSocketService.sendMessage({
      action: 'start_research',
      userId: researchData.userId,
      prompt: researchData.prompt,
      uploaded_documents: researchData.uploaded_documents || [],
      domain: researchData.domain,
      competitors: researchData.competitors,
      model: researchData.model,
      search_api: researchData.search_api
    });
  },

  sendClarificationWebSocket: (jobId, message) => {
    console.log('Sending clarification via WebSocket:', jobId, message);
    competitiveResearchWebSocketService.sendMessage({
      action: 'clarification_response',
      jobId: jobId,
      clarification: message
    });
  },

  approveResearchWebSocket: (jobId, brief, originalPrompt) => {
    console.log('Approving research via WebSocket:', jobId);
    competitiveResearchWebSocketService.sendMessage({
      action: 'approve_brief',
      jobId: jobId,
      brief: brief,
      user_prompt: originalPrompt
    });
  },

  listJobsWebSocket: (userId) => {
    console.log('Listing jobs via WebSocket for user:', userId);
    competitiveResearchWebSocketService.sendMessage({
      action: 'list_jobs',
      userId: userId
    });
  },

  retryJobWebSocket: (jobId) => {
    console.log('Retrying job via WebSocket:', jobId);
    competitiveResearchWebSocketService.sendMessage({
      action: 'retry_drafting',
      jobId: jobId
    });
  },

  getDownloadLinkWebSocket: (s3Url) => {
    console.log('Requesting download link via WebSocket:', s3Url);
    competitiveResearchWebSocketService.sendMessage({
      action: 'get_download_link',
      s3_url: s3Url
    });
  },

  setupWebSocketHandlers: (handlers, userId = null) => {
    competitiveResearchWebSocketService.connect(userId);
    
    const unsubscribe = competitiveResearchWebSocketService.onMessage('*', (message) => {
      console.log('WebSocket message received:', message.type);
      
      if (handlers[message.type]) {
        handlers[message.type](message);
      }
      
      if (handlers['*']) {
        handlers['*'](message);
      }
    });
    
    return unsubscribe;
  }
};

export default competitiveIntelligenceApi;